/**
 * "What moves the number": re-prices the project with one input nudged at a
 * time, so a push-back on duration, machine count or data volume can be
 * answered with a figure rather than a guess.
 *
 * Each variant is a real engine round-trip, which is why this runs on demand
 * rather than live. Levers run one after another (spreads within a lever go in
 * parallel, matching a normal page load) so a run never bursts the whole grid
 * of calls at the database at once.
 */
import { ProjectState, Spread, MACHINE_KEYS, MachineKey } from './spreadSplitData';
import { ModuleSource } from './spreadSourceData';
import { fetchSpreadPrice, SpreadRequestOpts } from './spreadPricingApi';
import { spreadResultTotal } from './spreadRollup';

const VOLUME_STEP = 0.1; // 10% either way on every data source
const MIN_MONTHS = 1;

export interface Variant {
  label: string;
  total: number;
  /** Signed difference from the base total. */
  delta: number;
  /** Signed share of the base total, 0..1. */
  deltaShare: number;
}

export interface LeverResult {
  key: string;
  label: string;
  variants: Variant[];
  /** Largest absolute move this lever produced. */
  swing: number;
}

export interface Sensitivity {
  base: number;
  levers: LeverResult[];
  /** Label of the lever with the biggest swing, or null when nothing moved. */
  biggestLever: string | null;
}

const mapSpreads = (project: ProjectState, fn: (s: Spread) => Spread): ProjectState =>
  ({ ...project, spreads: project.spreads.map(fn) });

/** Pin the spread to an explicit duration, shifted by the given months. */
function shiftDuration(spread: Spread, months: number, baseMonths: (s: Spread) => number): Spread {
  return {
    ...spread,
    throughputMode: 'duration',
    durationMonths: Math.max(MIN_MONTHS, baseMonths(spread) + months),
  };
}

/** The machine type carrying the most units, or null when the spread has none. */
function largestMachine(spread: Spread): MachineKey | null {
  const ranked = MACHINE_KEYS
    .filter((k) => spread.machines[k] > 0)
    .sort((a, b) => spread.machines[b] - spread.machines[a]);
  return ranked[0] ?? null;
}

function shiftMachines(spread: Spread, by: number): Spread {
  const key = largestMachine(spread);
  if (!key) return spread;
  const next = Math.max(0, spread.machines[key] + by);
  return { ...spread, machines: { ...spread.machines, [key]: next } };
}

const scaleSources = (sources: ModuleSource[], factor: number): ModuleSource[] =>
  sources.map((s) => ({ ...s, pipeCount: Math.max(0, Math.round(s.pipeCount * factor)) }));

function scaleVolume(spread: Spread, factor: number): Spread {
  return {
    ...spread,
    mtMills: scaleSources(spread.mtMills, factor),
    ndtSources: scaleSources(spread.ndtSources, factor),
    coatingSources: scaleSources(spread.coatingSources, factor),
  };
}

interface LeverDef {
  key: string;
  label: string;
  variants: { label: string; apply: (p: ProjectState) => ProjectState }[];
}

function leverDefs(baseMonths: (s: Spread) => number): LeverDef[] {
  return [
    {
      key: 'duration',
      label: 'Duration',
      variants: [
        { label: '1 month shorter', apply: (p) => mapSpreads(p, (s) => shiftDuration(s, -1, baseMonths)) },
        { label: '1 month longer', apply: (p) => mapSpreads(p, (s) => shiftDuration(s, 1, baseMonths)) },
      ],
    },
    {
      key: 'machines',
      label: 'Machines',
      variants: [
        { label: 'one fewer per spread', apply: (p) => mapSpreads(p, (s) => shiftMachines(s, -1)) },
        { label: 'one more per spread', apply: (p) => mapSpreads(p, (s) => shiftMachines(s, 1)) },
      ],
    },
    {
      key: 'volume',
      label: 'Data volume',
      variants: [
        { label: '10% fewer records', apply: (p) => mapSpreads(p, (s) => scaleVolume(s, 1 - VOLUME_STEP)) },
        { label: '10% more records', apply: (p) => mapSpreads(p, (s) => scaleVolume(s, 1 + VOLUME_STEP)) },
      ],
    },
  ];
}

/** Price every spread in a project variant and sum the results. */
async function priceProject(project: ProjectState, opts: SpreadRequestOpts): Promise<number> {
  const results = await Promise.all(
    project.spreads.map((spread) => fetchSpreadPrice(project, spread, opts))
  );
  return results.reduce((sum, r) => sum + spreadResultTotal(r), 0);
}

export interface SensitivityArgs {
  project: ProjectState;
  opts: SpreadRequestOpts;
  /** Base total to compare against, including project services. */
  baseTotal: number;
  /** Services are project-level and unaffected by these levers, so they are a
   *  constant added to every variant. */
  servicesTotal: number;
  /** Duration in months a spread currently prices at. */
  baseMonths: (s: Spread) => number;
  onProgress?: (done: number, total: number) => void;
}

/**
 * Re-price the project once per lever variant and report the movement.
 *
 * Levers run in sequence; only the spreads inside one variant go in parallel.
 */
export async function runSensitivity({
  project, opts, baseTotal, servicesTotal, baseMonths, onProgress,
}: SensitivityArgs): Promise<Sensitivity> {
  const defs = leverDefs(baseMonths);
  const totalRuns = defs.reduce((n, d) => n + d.variants.length, 0);
  let done = 0;

  const levers: LeverResult[] = [];
  for (const def of defs) {
    const variants: Variant[] = [];
    for (const variant of def.variants) {
      const total = (await priceProject(variant.apply(project), opts)) + servicesTotal;
      const delta = total - baseTotal;
      variants.push({
        label: variant.label,
        total,
        delta,
        deltaShare: baseTotal > 0 ? delta / baseTotal : 0,
      });
      done += 1;
      onProgress?.(done, totalRuns);
    }
    const swing = Math.max(...variants.map((v) => Math.abs(v.delta)), 0);
    levers.push({ key: def.key, label: def.label, variants, swing });
  }

  const ranked = [...levers].sort((a, b) => b.swing - a.swing);
  return {
    base: baseTotal,
    levers,
    biggestLever: ranked[0] && ranked[0].swing > 0 ? ranked[0].label : null,
  };
}
